import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Edit, Plus, Trash2, Image as ImageIcon, Smartphone, Apple } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/ui/avatar';
import { useBanners, useDeleteBanner } from '../model';
import { Banner } from '../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DataTable } from '@/shared/widgets';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { useTranslation } from '@/shared/i18n';

export const BannersList = () => {
  const { t } = useTranslation('banners');
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState<Banner | null>(null);

  const { banners, isLoading, error } = useBanners();
  const { deleteBanner, isLoading: isDeleting } = useDeleteBanner();

  const handleEdit = (id: string) => navigate(`/mobile/banners/${id}`);
  const handleDelete = (banner: Banner) => {
    setBannerToDelete(banner);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!bannerToDelete) return;
    try {
      await deleteBanner(bannerToDelete.id);
      setDeleteDialogOpen(false);
      setBannerToDelete(null);
    } catch (err) {
      console.error('Erro ao excluir banner:', err);
    }
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('banners')}</h1>
          <p className="text-muted-foreground">{t('manageBanners')}</p>
        </div>
        <Button onClick={() => navigate('/mobile/banners/new')}>
          <Plus className="mr-2 h-4 w-4" /> {t('newBanner')}
        </Button>
      </div>

      <DataTable
        data={banners}
        isLoading={isLoading}
        error={error}
        emptyTitle={t('emptyTitle')}
        emptyDescription={() => t('emptyDescription')}
        emptyActionLabel={t('newBanner')}
        emptyActionOnClick={() => navigate('/mobile/banners/new')}
        hasFilters={false}
        errorMessage={`${t('errorLoadingBanners')}: ${error?.message}`}
        columns={[
          {
            header: t('image'),
            cell: (item) => (
              <div className="flex items-center">
                {item.url ? (
                  <Avatar className="h-12 w-12 rounded-md">
                    <AvatarImage src={item.url} alt={item.description} className="object-cover" />
                    <AvatarFallback className="rounded-md bg-white/10">
                      <ImageIcon className="h-6 w-6 text-white/50" />
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <div className="h-12 w-12 rounded-md bg-white/10 flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-white/50" />
                  </div>
                )}
              </div>
            ),
            className: 'w-20',
          },
          {
            header: t('description'),
            cell: (item) => (
              <div className="flex flex-col gap-1">
                <span className="font-medium text-white">{item.description}</span>
                {item.redirectType && (
                  <span className="text-xs text-gray-400">{item.redirectType}</span>
                )}
              </div>
            ),
            className: 'min-w-[200px]',
          },
          {
            header: t('order'),
            cell: (item) => <span className="text-sm text-gray-300">{item.displayOrder}</span>,
          },
          {
            header: t('minVersion'),
            cell: (item) => (
              <div className="flex flex-col gap-0.5 text-sm">
                <span className="flex items-center gap-1 text-gray-300">
                  <Smartphone className="h-3 w-3" /> {item.minBuildAndroid ?? '-'}
                </span>
                <span className="flex items-center gap-1 text-gray-300">
                  <Apple className="h-3 w-3" /> {item.minBuildIos ?? '-'}
                </span>
              </div>
            ),
            className: 'min-w-[120px]',
          },
          {
            header: t('status'),
            cell: (item) => (
              <Badge
                variant={item.isActive ? 'default' : 'secondary'}
                className={item.isActive ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'}
              >
                {item.isActive ? t('active') : t('inactive')}
              </Badge>
            ),
          },
          {
            header: t('createdAt'),
            cell: (item) => (
              <span className="text-sm text-gray-400">{formatDateTime(item.createdAt)}</span>
            ),
            className: 'min-w-[140px]',
          },
          {
            header: t('actions'),
            cell: (item) => (
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(item.id)}
                  title={t('edit')}
                  className="hover:bg-white/10"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(item)}
                  title={t('delete')}
                  className="hover:bg-destructive/20"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ),
            className: 'text-right w-24',
          },
        ]}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteBanner')}?</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteBannerMessage', { name: bannerToDelete?.description || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('deleting') : t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
